import React, { useEffect, useState, useRef } from "react";
import { useTable } from "react-table";
import Sidebar from "../layout/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import {
  addAddress,
  deleteAddress,
  editAddress,
  fetchAddresses,
} from "../../redux/features/addressSlice";
import { Plus, Save, Edit3, Trash2 } from "lucide-react";
import { Delete, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const columns = [
  { Header: "Name", accessor: "name" },
  { Header: "Mobile Number", accessor: "mobile" },

  {
    Header: "Address",
    accessor: (row) => {
      const house = row.house_no || "";
      const street = row.street || "";
      const combined = [house, street].filter(Boolean).join(", ");
      return combined || "-";
    },
  },

  { Header: "City", accessor: (row) => row.city || "-" },
  { Header: "State", accessor: (row) => row.state || "-" },
  { Header: "Country", accessor: (row) => row.country || "-" },
  { Header: "Pincode", accessor: (row) => row.pincode || "-" },

  {
    Header: "Default",
    accessor: (row) => (row.is_default === 1 ? "Yes" : "No"),
  },
];

const Address = () => {
  const { t } = useTranslation()
  const { addresses } = useSelector((state) => state.address);
  const [formData, setFormData] = useState({
    id: null,
    // address: "",
    // address_title: "",
    city: "",
    state: "",
    country: "",
    email: "",
    house_no: "",
    street: "",
    mobile: "",
    name: "",
    pincode: "",
    // type: "1",
    is_default: "0",
  });

  const dispatch = useDispatch();
  const formRef = useRef(null);

  const [errors, setErrors] = useState({});

  // 🔥 VALIDATION RULES
  const validators = {
    name: (v) => (v.length < 3 ? "Name must be at least 3 characters." : ""),
    street: (v) => (v.length < 3 ? "Street must be at least 3 characters." : ""),
    house_no: (v) => (!v ? "House number is required." : ""),
    city: (v) =>
      !/^[A-Za-z ]+$/.test(v) ? "City must contain only letters." : "",
    state: (v) =>
      !/^[A-Za-z ]+$/.test(v) ? "State must contain only letters." : "",
    country: (v) =>
      !/^[A-Za-z ]+$/.test(v) ? "Country must contain only letters." : "",
    pincode: (v) =>
      !/^[0-9]{5,6}$/.test(v) ? "Pincode must be 5–6 digits." : "",
    mobile: (v) =>
      !/^[0-9]{10}$/.test(v) ? "Mobile number must be exactly 10 digits." : "",
    email: (v) =>
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? "Enter a valid email." : "",
  };

  // 🔥 Validate single field on type
  const validateField = (name, value) => {
    if (validators[name]) {
      const errorMsg = validators[name](value);
      setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    }
  };

  useEffect(() => {
    dispatch(fetchAddresses());
  }, [dispatch]);

  const formattedAddresses = addresses.map((address) => ({
    ...address,
    formattedAddress: [
      address.house_no,
      address.street,
      address.city,
      address.state,
      address.country,
      address.pincode,
    ]
      .filter(Boolean)
      .join(", "),
  }));


  const { rows, prepareRow } = useTable({
    columns,
    data: formattedAddresses,
  });

  // 🔥 Live field validation
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    validateField(name, finalValue);
  };

  // 🔥 Validate full form
  const isFormValid = () => {
    const newErrors = {};
    Object.keys(validators).forEach((key) => {
      const error = validators[key](formData[key]);
      if (error) newErrors[key] = error;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetFormData = () => {
    setFormData({
      id: null,
      // address: "",
      // address_title: "",
      city: "",
      state: "",
      country: "",
      email: "",
      house_no: "",
      street: "",
      mobile: "",
      name: "",
      pincode: "",
      // type: "1",
      is_default: "0",
    });

    // Smoothly scroll back to form if visible
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // 🔥 Submit
  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    if (formData.id) {
      dispatch(editAddress(formData)).then(() => dispatch(fetchAddresses()));
    } else {
      dispatch(addAddress(formData)).then(() => dispatch(fetchAddresses()));
    }
    resetFormData();
  };

  // 🔥 Edit
  const handleEdit = (address) => {
    setFormData({
      id: address.id,
      name: address.name,
      street: address.street,
      house_no: address.house_no,
      city: address.city,
      state: address.state,
      country: address.country,
      pincode: address.pincode,
      mobile: address.mobile,
      email: address.email,
      is_default: address.is_default,
    });

    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // 🔥 Delete
  const handleDelete = (address) => {
    if (window.confirm("Delete this address?")) {
      dispatch(deleteAddress(address.id));
    }
  };

  return (
    <section className="pt-5 pb-5 bg-light min-vh-100">
      <div className="container">
        <div className="row g-4">
          {/* Sidebar */}
          <div className="col-lg-3">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-9">

            {/* Address List */}
            <div className="row g-4">
              {rows.length > 0 ? (
                rows.map((row) => {
                  prepareRow(row);
                  const data = row.original;

                  return (
                    <div className="col-md-6 col-lg-4" key={data.id || Math.random()}>
                      <div
                        className="card bg-white rounded-4 h-100 position-relative overflow-hidden address-card"
                        style={{
                          transition: "all 0.3s ease",
                          border: data.is_default === 1 ? "2px solid #005062" : "none",
                          boxShadow:
                            data.is_default === 1
                              ? "0 0 10px 3px rgba(0, 80, 98, 0.4)"
                              : "none",
                          borderRadius: "16px", // match your rounded-4
                        }}
                      >
                        {/* Edit / Delete Buttons */}
                        <div className="position-absolute top-0 end-0 m-2 d-flex gap-2">
                          <button
                            className="btn rounded-2 shadow-sm border-0 d-flex align-items-center justify-content-center"
                            onClick={() => handleEdit(data)}
                            title="Edit"
                          >
                            <Edit className="text-primary" />
                          </button>
                          <button
                            className="btn rounded-2 shadow-sm border-0 d-flex align-items-center justify-content-center"
                            onClick={() => handleDelete(data)}
                            title="Delete"
                          >
                            <Delete className="text-danger" />
                          </button>
                        </div>

                        {/* Card Body */}
                        <div className="card-body mt-5">
                          <h6 className="fw-semibold mb-2 text-dark">
                            {data.name || "Unnamed"}
                          </h6>

                          {/* Mobile */}
                          <p className="text-muted small mb-1">
                            <i className="bi bi-telephone me-1"></i>
                            {data.mobile || "-"}
                          </p>

                          {/* Address (house_no + street) */}
                          <p className="text-muted small mb-1">
                            <i className="bi bi-geo-alt me-1"></i>
                            {[data.house_no, data.street].filter(Boolean).join(", ") || "-"}
                          </p>

                          {/* City, State, Country, Pincode */}
                          <p className="text-muted small mb-4">
                            {[data.city, data.state, data.country].filter(Boolean).join(", ") || "-"}
                            {data.pincode ? ` - ${data.pincode}` : ""}
                          </p>

                          {/* Remove data.type badge completely */}

                          {/* Default Badge */}
                          {data.is_default === 1 && (
                            <span
                              className="default-badge-profile"
                              style={{
                                background: "rgba(0, 80, 98)",
                                color: "#fff",
                                padding: "4px 12px",
                                borderRadius: "50px",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                letterSpacing: "0.3px",
                              }}
                            >
                              Default
                            </span>
                          )}
                        </div>

                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-muted mt-4">{t("No_addresses_found")}.</p>
              )}
            </div>

            {/* Address Form */}
            <div ref={formRef} className="card bg-white shadow-sm p-4 mt-4 rounded-4">
              <h4 className="fw-bold mb-3">
                {formData.id ? t("Edit_Address") : t("Add_New_Address")}
              </h4>

              <form className="row g-3" onSubmit={handleFormSubmit}>
                {/* Name */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("Name")} *</label>
                  <input
                    type="text"
                    name={t("Name")}
                    className={`form-control rounded-3 ${errors.name ? "is-invalid" : ""
                      }`}
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t("Full_Name")}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                {/* Street */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("Street")} *</label>
                  <input
                    type="text"
                    name="street"
                    className={`form-control rounded-3 ${errors.street ? "is-invalid" : ""
                      }`}
                    value={formData.street}
                    onChange={handleChange}
                    placeholder={t("Street")}
                  />
                  {errors.street && (
                    <div className="invalid-feedback">{errors.street}</div>
                  )}
                </div>

                {/* House No */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("House_No.")} *</label>
                  <input
                    type="text"
                    name="house_no"
                    className={`form-control rounded-3 ${errors.house_no ? "is-invalid" : ""
                      }`}
                    value={formData.house_no}
                    onChange={handleChange}
                    placeholder={t("House_No.")}
                  />
                  {errors.house_no && (
                    <div className="invalid-feedback">{errors.house_no}</div>
                  )}
                </div>

                {/* Pincode */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("Zip_Code")} *</label>
                  <input
                    type="text"
                    name="pincode"
                    className={`form-control rounded-3 ${errors.pincode ? "is-invalid" : ""
                      }`}
                    value={formData.pincode}
                    onChange={handleChange}
                    placeholder={t("Zip_Code")}
                  />
                  {errors.pincode && (
                    <div className="invalid-feedback">{errors.pincode}</div>
                  )}
                </div>

                {/* City */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("City")} *</label>
                  <input
                    type="text"
                    name="city"
                    className={`form-control rounded-3 ${errors.city ? "is-invalid" : ""
                      }`}
                    value={formData.city}
                    onChange={handleChange}
                    placeholder={t("City")}
                  />
                  {errors.city && (
                    <div className="invalid-feedback">{errors.city}</div>
                  )}
                </div>

                {/* State */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("State")} *</label>
                  <input
                    type="text"
                    name="state"
                    className={`form-control rounded-3 ${errors.state ? "is-invalid" : ""
                      }`}
                    value={formData.state}
                    onChange={handleChange}
                    placeholder={t("State")}
                  />
                  {errors.state && (
                    <div className="invalid-feedback">{errors.state}</div>
                  )}
                </div>

                {/* Country */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("Country")} *</label>
                  <input
                    type="text"
                    name="country"
                    className={`form-control rounded-3 ${errors.country ? "is-invalid" : ""
                      }`}
                    value={formData.country}
                    onChange={handleChange}
                    placeholder={t("Country")}
                  />
                  {errors.country && (
                    <div className="invalid-feedback">{errors.country}</div>
                  )}
                </div>

                {/* Mobile */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("Mobile")} *</label>
                  <input
                    type="text"
                    name="mobile"
                    className={`form-control rounded-3 ${errors.mobile ? "is-invalid" : ""
                      }`}
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder={t("Mobile_Number")}
                  />
                  {errors.mobile && (
                    <div className="invalid-feedback">{errors.mobile}</div>
                  )}
                </div>

                {/* Email */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">{t("Email")} *</label>
                  <input
                    type="email"
                    name="email"
                    className={`form-control rounded-3 ${errors.email ? "is-invalid" : ""
                      }`}
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={t("Email")}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                {/* Default */}
                <div className="col-md-12 d-flex align-items-center mt-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      name="is_default"
                      className="form-check-input"
                      checked={formData.is_default === 1}
                      onChange={handleChange}
                    />
                    <label className="form-check-label fw-semibold ms-2">
                      {t("Set_as_Default")}
                    </label>
                  </div>
                </div>

                <div className="col-12 text-end">
                  <button type="submit" className="btn btn-primary rounded-pill px-4">
                    {t("Save_Address")}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Address;
